import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import pacienteService from '../../services/pacienteService';
import api from '../../services/api';
import { criarFaturamento } from '../../services/faturamentoService';
import { useAuth } from '../../context/AuthContext';
import Odontograma from '../../components/Odontograma';
import RostoEstetico from '../../components/RostoEstetico';
import clinicaService from '../../services/clinicaService';
import ModalPagamento from '../../components/ModalPagamento';
import ModalOrcamento from '../../components/ModalOrcamento';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import {
  Box, Typography, Button, Chip, Avatar, CircularProgress,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  TextField, Select, MenuItem, FormControl, InputLabel, Alert,
  Tabs, Tab,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import MedicationIcon from '@mui/icons-material/Medication';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FolderIcon from '@mui/icons-material/Folder';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import HistoryIcon from '@mui/icons-material/History';

const ORANGE = '#F97316';

const statusConfig = {
  concluido:  { label: 'Concluído',  color: '#16a34a', bg: '#dcfce7' },
  agendado:   { label: 'Agendado',   color: '#2563eb', bg: '#dbeafe' },
  cancelado:  { label: 'Cancelado',  color: '#dc2626', bg: '#fee2e2' },
  compareceu: { label: 'Compareceu', color: '#7c3aed', bg: '#ede9fe' },
};

const STATUS_ORCAMENTO = {
  fechado:     { label: 'Fechado',            color: '#16a34a', bg: '#dcfce7' },
  nao_fechado: { label: 'Não Fechou',          color: '#dc2626', bg: '#fee2e2' },
  retornar:    { label: 'Ficou de Retornar',   color: '#F97316', bg: '#fff7ed' },
  pendente:    { label: 'Pendente',            color: '#64748b', bg: '#f1f5f9' },
};

const DENTES_SUPERIOR = [18,17,16,15,14,13,12,11,21,22,23,24,25,26,27,28];
const DENTES_INFERIOR = [48,47,46,45,44,43,42,41,31,32,33,34,35,36,37,38];

const Dente = ({ numero }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, p: '6px 2px', borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(249,115,22,0.08)' }, transition: '0.15s' }}>
    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled' }}>{numero}</Typography>
    <i className="fa-solid fa-tooth" style={{ fontSize: numero >= 13 && numero <= 23 ? '1rem' : '1.25rem', color: '#cbd5e0' }} />
  </Box>
);

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData = (d) => d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

const FichaPaciente = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useAppTheme();
  const { user } = useAuth();
  const tipoUsuario = user?.tipoPessoa || 'PF';

  const [paciente, setPaciente] = useState(null);
  const [aba, setAba] = useState('odontograma');
  const [especialidades, setEspecialidades] = useState({ atendeOdontologia: true, atendeEstetica: false });

  const tabsDisponiveis = [
    especialidades.atendeOdontologia && { key: 'odontograma', label: 'Odontograma', icon: <MedicalServicesIcon sx={{ fontSize: 18 }} /> },
    especialidades.atendeEstetica && { key: 'estetica', label: 'Estética', icon: <FaceRetouchingNaturalIcon sx={{ fontSize: 18 }} /> },
    { key: 'historico', label: 'Histórico', icon: <HistoryIcon sx={{ fontSize: 18 }} /> },
    { key: 'orcamentos', label: 'Orçamentos', icon: <ReceiptLongIcon sx={{ fontSize: 18 }} /> },
    { key: 'financeiro', label: 'Financeiro', icon: <MonetizationOnIcon sx={{ fontSize: 18 }} /> },
    { key: 'documentos', label: 'Documentos', icon: <FolderIcon sx={{ fontSize: 18 }} /> },
    { key: 'observacoes', label: 'Observações', icon: <EditNoteIcon sx={{ fontSize: 18 }} /> },
  ].filter(Boolean);

  // Garante que a aba selecionada continue válida quando os toggles de especialidade mudam
  useEffect(() => {
    if (!tabsDisponiveis.some(t => t.key === aba)) {
      setAba(tabsDisponiveis[0]?.key || 'historico');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [especialidades]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historico, setHistorico] = useState([]);
  const [financeiro, setFinanceiro] = useState({
    totalInvestido: 0, totalNFs: 0, totalPendentes: 0, saldoEmAberto: 0, historico: [], porMes: [],
  });

  // Todos os orçamentos do paciente (histórico completo, aba Orçamentos)
  const [orcamentos, setOrcamentos] = useState([]);
  // Orçamentos em aberto (saldo pendente) e registro de pagamento complementar
  const [orcamentosAbertos, setOrcamentosAbertos] = useState([]);
  const [procedimentosLista, setProcedimentosLista] = useState([]);
  const [procedimentosMap, setProcedimentosMap] = useState({});
  const [orcamentoSelecionado, setOrcamentoSelecionado] = useState(null);
  const [modalPagamentoAberto, setModalPagamentoAberto] = useState(false);
  // Criar/editar orçamento (aba Orçamentos)
  const [modalOrcamentoAberto, setModalOrcamentoAberto] = useState(false);
  const [orcamentoEmEdicao, setOrcamentoEmEdicao] = useState(null);
  // Orçamento recém-fechado, aguardando a pergunta "quer marcar a consulta agora?"
  const [orcamentoParaAgendar, setOrcamentoParaAgendar] = useState(null);

  // Termos assinados
  const [termosAssinados, setTermosAssinados] = useState([]);

  // Histórico de anotações/observações (uma por consulta)
  const [anotacoes, setAnotacoes] = useState([]);
  const [novaAnotacao, setNovaAnotacao] = useState('');
  const [salvandoAnotacao, setSalvandoAnotacao] = useState(false);
  const [erroAnotacao, setErroAnotacao] = useState('');

  // Pastas e arquivos do paciente (fotos, raio-x, contratos, etc)
  const [arquivos, setArquivos] = useState([]);
  const [novaPastaNome, setNovaPastaNome] = useState('');
  const [criandoPasta, setCriandoPasta] = useState(false);
  const [uploadingPasta, setUploadingPasta] = useState(null);
  const [erroArquivo, setErroArquivo] = useState('');

  // Enviar anamnese via Autentique
  const [termoAnamneses, setTermoAnamneses] = useState([]);
  const [modalEnviarAnamnese, setModalEnviarAnamnese] = useState(false);
  const [envTermoId, setEnvTermoId] = useState('');
  const [envVia, setEnvVia] = useState('link');
  const [enviando, setEnviando] = useState(false);
  const [erroEnviar, setErroEnviar] = useState('');
  const [linkGerado, setLinkGerado] = useState('');

  const carregarTudo = useCallback(async () => {
    try {
      const [pac, hist, fin, termosResp, docsResp, orcamentosResp, procedimentosResp, anotacoesResp, arquivosResp] = await Promise.all([
        pacienteService.buscarPorId(id),
        pacienteService.historico(id).catch(() => []),
        api.get(`/faturamento/paciente/${id}/resumo`).catch(() => ({ totalInvestido: 0, saldoEmAberto: 0, historico: [], porMes: [] })),
        api.get('/termos').catch(() => []),
        api.get(`/termos/documentos?pacienteId=${id}`).catch(() => []),
        api.get(`/orcamentos/paciente/${id}`).catch(() => []),
        api.get('/procedimentos').catch(() => []),
        api.get(`/pacientes/${id}/anotacoes`).catch(() => []),
        api.get(`/pacientes/${id}/arquivos`).catch(() => []),
      ]);
      setPaciente(pac);
      setHistorico(Array.isArray(hist) ? hist : []);
      setFinanceiro(fin || { totalInvestido: 0, totalNFs: 0, totalPendentes: 0, saldoEmAberto: 0, historico: [], porMes: [] });
      const listaTermos = Array.isArray(termosResp) ? termosResp : Array.isArray(termosResp?.data) ? termosResp.data : [];
      setTermoAnamneses(listaTermos.filter(t => t.tipo === 'anamnese'));
      const listaDocs = Array.isArray(docsResp) ? docsResp : Array.isArray(docsResp?.data) ? docsResp.data : [];
      setTermosAssinados(listaDocs.filter(d => d.status === 'assinado'));
      const listaOrcamentos = Array.isArray(orcamentosResp) ? orcamentosResp : Array.isArray(orcamentosResp?.data) ? orcamentosResp.data : [];
      setOrcamentos(listaOrcamentos);
      setOrcamentosAbertos(listaOrcamentos.filter(o => o.status === 'fechado' && (o.saldoAberto || 0) > 0));
      const listaProcedimentos = Array.isArray(procedimentosResp) ? procedimentosResp : Array.isArray(procedimentosResp?.data) ? procedimentosResp.data : [];
      setProcedimentosLista(listaProcedimentos);
      setProcedimentosMap(Object.fromEntries(listaProcedimentos.map(p => [p.id, p.nome])));
      setAnotacoes(Array.isArray(anotacoesResp) ? anotacoesResp : Array.isArray(anotacoesResp?.data) ? anotacoesResp.data : []);
      setArquivos(Array.isArray(arquivosResp) ? arquivosResp : Array.isArray(arquivosResp?.data) ? arquivosResp.data : []);
    } catch (e) {
      setError('Erro ao carregar ficha do paciente.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { carregarTudo(); }, [carregarTudo]);

  // Especialidades da clínica — controla se Odontograma e/ou Mapa Estético aparecem
  useEffect(() => {
    if (!user?.clinicaId) return;
    clinicaService.buscarClinica(user.clinicaId)
      .then(clinica => setEspecialidades({
        atendeOdontologia: clinica?.atendeOdontologia ?? true,
        atendeEstetica: clinica?.atendeEstetica ?? false,
      }))
      .catch(() => {});
  }, [user?.clinicaId]);

  const handleEnviarAnamnese = async () => {
    if (!envTermoId) { setErroEnviar('Selecione uma anamnese'); return; }
    setEnviando(true);
    setErroEnviar('');
    setLinkGerado('');
    try {
      const resp = await api.post('/termos/enviar', { pacienteId: id, termoId: envTermoId, via: envVia });
      const link = resp?.data?.link || resp?.link;
      setLinkGerado(link || '');
      await carregarTudo();
    } catch (e) {
      setErroEnviar(e?.response?.data?.error || 'Erro ao enviar anamnese.');
    } finally {
      setEnviando(false);
    }
  };

  const handleAdicionarAnotacao = async () => {
    if (!novaAnotacao.trim()) return;
    setSalvandoAnotacao(true);
    setErroAnotacao('');
    try {
      const criada = await api.post(`/pacientes/${id}/anotacoes`, { texto: novaAnotacao.trim() });
      setAnotacoes(prev => [criada, ...prev]);
      setNovaAnotacao('');
    } catch (e) {
      setErroAnotacao('Erro ao salvar anotação.');
    } finally {
      setSalvandoAnotacao(false);
    }
  };

  const handleCriarPasta = async () => {
    if (!novaPastaNome.trim()) return;
    setCriandoPasta(true);
    setErroArquivo('');
    try {
      const pasta = await api.post(`/pacientes/${id}/pastas`, { pasta: novaPastaNome.trim() });
      setArquivos(prev => [...prev, pasta]);
      setNovaPastaNome('');
    } catch (e) {
      setErroArquivo('Erro ao criar pasta.');
    } finally {
      setCriandoPasta(false);
    }
  };

  const handleUploadArquivo = async (pasta, file) => {
    if (!file) return;
    setUploadingPasta(pasta);
    setErroArquivo('');
    try {
      const formData = new FormData();
      formData.append('arquivo', file);
      const novo = await api.post(`/pacientes/${id}/arquivos?pasta=${encodeURIComponent(pasta)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setArquivos(prev => [...prev, novo]);
    } catch (e) {
      setErroArquivo(e?.response?.data?.error || 'Erro ao enviar arquivo.');
    } finally {
      setUploadingPasta(null);
    }
  };

  const handleExcluirArquivo = async (arquivoId) => {
    if (!window.confirm('Excluir este arquivo? Essa ação não pode ser desfeita.')) return;
    try {
      await api.delete(`/pacientes/${id}/arquivos/${arquivoId}`);
      setArquivos(prev => prev.filter(a => a.id !== arquivoId));
    } catch (e) {
      setErroArquivo('Erro ao excluir arquivo.');
    }
  };

  const handleExcluirPasta = async (pasta) => {
    if (!window.confirm(`Excluir a pasta "${pasta}" e todos os arquivos dentro dela? Essa ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/pacientes/${id}/pastas/${encodeURIComponent(pasta)}`);
      setArquivos(prev => prev.filter(a => (a.pasta || 'Geral') !== pasta));
    } catch (e) {
      setErroArquivo('Erro ao excluir pasta.');
    }
  };

  const handleBaixarArquivo = async (arquivoId) => {
    try {
      const { url } = await api.get(`/pacientes/${id}/arquivos/${arquivoId}/download`);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setErroArquivo('Erro ao baixar arquivo.');
    }
  };

  const handleAbrirPagamento = (orcamento) => {
    setOrcamentoSelecionado(orcamento);
    setModalPagamentoAberto(true);
  };

  const handleSalvarPagamentoOrcamento = async (data) => {
    const formaPagamentoMap = {
      dinheiro: 'Dinheiro',
      cartao: 'Cartão de Crédito',
      pix: 'PIX',
      boleto: 'Outros',
      outro: 'Outros',
    };
    const nomesProcedimentos = (orcamentoSelecionado?.procedimentos || [])
      .map(pid => procedimentosMap[pid])
      .filter(Boolean);
    const usuarioLogado = JSON.parse(localStorage.getItem('user') || 'null');
    const payload = {
      descricao: nomesProcedimentos.length ? nomesProcedimentos.join(', ') : 'Pagamento de saldo em aberto',
      valor: data.valor,
      data: new Date().toISOString().slice(0, 10),
      formaPagamento: formaPagamentoMap[data.forma] || 'Dinheiro',
      paciente: paciente?.nome || '',
      cpf: paciente?.cpfCnpj || '',
      procedimento: nomesProcedimentos[0] || '',
      tipoPessoa: data.tipoPessoa || tipoUsuario,
      observacoes: data.observacoes,
      pacienteId: paciente?.id || id,
      userId: usuarioLogado?.id,
      orcamentoId: orcamentoSelecionado?.id || null,
      ...(data.maquinaCartaoId && {
        maquinaCartaoId: data.maquinaCartaoId,
        parcelasCartao: data.parcelasCartao,
        cartaoAntecipado: data.cartaoAntecipado,
        taxaCartaoResponsavel: data.taxaCartaoResponsavel,
      }),
    };
    await criarFaturamento(payload);
    setOrcamentoSelecionado(null);
    await carregarTudo();
  };

  const handleAbrirNovoOrcamento = () => {
    setOrcamentoEmEdicao(null);
    setModalOrcamentoAberto(true);
  };

  const handleAbrirEditarOrcamento = (orc) => {
    setOrcamentoEmEdicao(orc);
    setModalOrcamentoAberto(true);
  };

  const handleSalvarOrcamento = async (data) => {
    const payload = {
      status: data.status,
      procedimentos: data.procedimentos,
      valores: data.valores,
      observacoes: data.observacoes,
    };
    let salvo;
    if (orcamentoEmEdicao?.id) {
      salvo = await api.put(`/orcamentos/${orcamentoEmEdicao.id}`, payload);
    } else {
      salvo = await api.post('/orcamentos', { ...payload, paciente_id: id });
    }
    setModalOrcamentoAberto(false);
    await carregarTudo();
    // Fechou agora e ainda não tem consulta marcada pra esse orçamento? Oferece agendar —
    // o pagamento é registrado depois, quando o paciente comparecer de verdade (ver Agenda).
    if (data.status === 'fechado' && salvo?.id && !salvo.agendamento) {
      setOrcamentoParaAgendar(salvo);
    }
  };

  const handleExcluirOrcamento = async (orcamentoId) => {
    if (!orcamentoId) { setModalOrcamentoAberto(false); return; }
    try {
      await api.delete(`/orcamentos/${orcamentoId}`);
      setModalOrcamentoAberto(false);
      setOrcamentoEmEdicao(null);
      await carregarTudo();
    } catch (e) {
      alert('Erro ao excluir orçamento.');
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress sx={{ color: ORANGE }} /></Box>;
  if (error || !paciente) return <Box p={4}><Typography color="error">{error || 'Paciente não encontrado.'}</Typography></Box>;

  const iniciais = paciente.nome?.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase() || '?';
  const dataCadastro = paciente.createdAt ? new Date(paciente.createdAt).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }) : '';

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/pacientes')} sx={{ mb: 2, color: 'text.secondary', fontWeight: 600 }}>
        Voltar
      </Button>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid rgba(249,115,22,0.15)', borderTop: '4px solid #F97316', boxShadow: '0 2px 12px rgba(249,115,22,0.08)' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: ORANGE, fontSize: '1.4rem', fontWeight: 700 }}>{iniciais}</Avatar>
            <Box>
              <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>{paciente.nome}</Typography>
              <Box display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
                {dataCadastro && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <CalendarMonthIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.disabled">Paciente desde {dataCadastro}</Typography>
                  </Box>
                )}
                {paciente.telefone && <Chip label={paciente.telefone} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />}
                {paciente.email && <Chip label={paciente.email} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />}
                <Chip label={paciente.ativo ? 'Ativo' : 'Inativo'} size="small" sx={{ fontSize: '0.7rem', height: 20, bgcolor: paciente.ativo ? '#dcfce7' : '#fee2e2', color: paciente.ativo ? '#16a34a' : '#dc2626', fontWeight: 700 }} />
              </Box>
            </Box>
          </Box>
          <Box display="flex" gap={1.5} flexWrap="wrap">
            <Button variant="outlined" startIcon={<DescriptionIcon />} onClick={() => navigate(`/pacientes/${paciente.id}/documento/atestado`)} sx={{ borderColor: '#2563eb', color: '#2563eb', borderRadius: 2, fontWeight: 600, '&:hover': { bgcolor: '#dbeafe' } }}>Atestado</Button>
            <Button variant="outlined" startIcon={<MedicationIcon />} onClick={() => navigate(`/pacientes/${paciente.id}/documento/receita`)} sx={{ borderColor: '#059669', color: '#059669', borderRadius: 2, fontWeight: 600, '&:hover': { bgcolor: '#d1fae5' } }}>Receita</Button>
          </Box>
        </Box>
      </Paper>

      {/* Abas */}
      <Paper sx={{ borderRadius: 3, border: '1px solid rgba(249,115,22,0.12)', mb: 3, overflow: 'hidden' }}>
        <Tabs
          value={aba}
          onChange={(e, v) => setAba(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 48,
            borderBottom: '1px solid rgba(249,115,22,0.12)',
            '& .MuiTabs-indicator': { backgroundColor: ORANGE, height: 3 },
            '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' },
            '& .Mui-selected': { color: `${ORANGE} !important` },
          }}
        >
          {tabsDisponiveis.map(t => (
            <Tab key={t.key} value={t.key} icon={t.icon} iconPosition="start" label={t.label} />
          ))}
        </Tabs>
      </Paper>

      {/* Aba: Odontograma */}
      {aba === 'odontograma' && especialidades.atendeOdontologia && (
        <Box display="flex" flexDirection="column" gap={3}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(249,115,22,0.12)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Typography fontWeight={700} fontSize="1rem" sx={{ borderLeft: '3px solid #F97316', pl: 1.5, mb: 2 }}>Odontograma</Typography>
            <Odontograma
              pacienteId={id}
              pacienteNome={paciente.nome}
              onAgendar={({ dente, procedimento, pacienteNome }) =>
                navigate('/agenda', { state: { paciente: pacienteNome, procedimento, observacao: `Dente ${dente}` } })
              }
            />
          </Paper>
        </Box>
      )}

      {/* Aba: Estética */}
      {aba === 'estetica' && especialidades.atendeEstetica && (
        <Box display="flex" flexDirection="column" gap={3}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(249,115,22,0.12)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Typography fontWeight={700} fontSize="1rem" sx={{ borderLeft: '3px solid #F97316', pl: 1.5, mb: 2 }}>Mapa Estético</Typography>
            <RostoEstetico
              pacienteId={id}
              pacienteNome={paciente.nome}
              onAgendar={({ regiao, procedimento, pacienteNome }) =>
                navigate('/agenda', { state: { paciente: pacienteNome, procedimento, observacao: regiao } })
              }
            />
          </Paper>
        </Box>
      )}

      {/* Aba: Histórico */}
      {aba === 'historico' && (
        <Box display="flex" flexDirection="column" gap={3}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(249,115,22,0.12)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Typography fontWeight={700} fontSize="1rem" sx={{ borderLeft: '3px solid #F97316', pl: 1.5, mb: 2.5 }}>Histórico de Procedimentos</Typography>
            {historico.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <HourglassEmptyIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.disabled">Nenhum procedimento registrado ainda.</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Data', 'Procedimento', 'Valor', 'Status'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', color: 'text.disabled' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historico.map(item => {
                      const sc = statusConfig[item.status] || { label: item.status || '-', color: '#64748b', bg: '#f1f5f9' };
                      return (
                        <TableRow key={item.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                            {item.data ? new Date(item.data).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}
                          </TableCell>
                          <TableCell>
                            <Typography fontSize="0.9rem" fontWeight={600}>{item.procedimento || '-'}</Typography>
                            {item.descricao && <Typography fontSize="0.75rem" color="text.secondary">{item.descricao}</Typography>}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: ORANGE, whiteSpace: 'nowrap' }}>{item.valor ? fmtBRL(item.valor) : '-'}</TableCell>
                          <TableCell><Chip label={sc.label} size="small" sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 700, fontSize: '0.72rem' }} /></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>
      )}

      {/* Aba: Orçamentos */}
      {aba === 'orcamentos' && (
        <Box display="flex" flexDirection="column" gap={3}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(249,115,22,0.12)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5} flexWrap="wrap" gap={1.5}>
              <Typography fontWeight={700} fontSize="1rem" sx={{ borderLeft: '3px solid #F97316', pl: 1.5 }}>Orçamentos</Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<ReceiptLongIcon />}
                onClick={handleAbrirNovoOrcamento}
                sx={{ bgcolor: ORANGE, borderRadius: 2, fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#ea6c0a' } }}
              >
                Novo Orçamento
              </Button>
            </Box>

            {orcamentos.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <ReceiptLongIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.disabled">Nenhum orçamento criado ainda.</Typography>
                <Typography color="text.disabled" fontSize="0.8rem" mt={0.5}>Clique em "Novo Orçamento" pra montar um.</Typography>
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" gap={1}>
                {orcamentos.map((orc) => {
                  const sc = STATUS_ORCAMENTO[orc.status] || STATUS_ORCAMENTO.pendente;
                  const nomesProcedimentos = (orc.procedimentos || []).map(pid => procedimentosMap[pid]).filter(Boolean).join(', ');
                  return (
                    <Box
                      key={orc.id}
                      onClick={() => handleAbrirEditarOrcamento(orc)}
                      sx={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, p: 1.5,
                        borderRadius: 2, border: '1px solid', borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
                        cursor: 'pointer', transition: 'all 0.15s',
                        '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(249,115,22,0.04)', borderColor: 'rgba(249,115,22,0.3)' },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontSize="0.88rem" fontWeight={600} noWrap>{nomesProcedimentos || 'Sem procedimentos definidos'}</Typography>
                        <Typography fontSize="0.75rem" color="text.disabled">
                          {fmtData(orc.createdAt)} • {fmtBRL(orc.valorTotal)}
                          {orc.status === 'fechado' && orc.saldoAberto > 0 && ` • ${fmtBRL(orc.saldoAberto)} em aberto`}
                        </Typography>
                        {orc.status === 'fechado' && (
                          orc.agendamento ? (
                            <Box display="flex" alignItems="center" gap={0.5} mt={0.3}>
                              <CalendarMonthIcon sx={{ fontSize: 13, color: '#2563eb' }} />
                              <Typography fontSize="0.72rem" fontWeight={600} color="#2563eb">
                                Consulta marcada pra {new Date(orc.agendamento.data_hora).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' })} às {new Date(orc.agendamento.data_hora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography fontSize="0.72rem" color="#F97316" mt={0.3}>Ainda não agendou a consulta</Typography>
                          )
                        )}
                      </Box>
                      <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
                        {orc.status === 'fechado' && !orc.agendamento && (
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/agenda', { state: { novoAgendamento: { pacienteId: id, procedimentos: orc.procedimentos, orcamentoId: orc.id } } });
                            }}
                            sx={{ color: ORANGE, fontSize: '0.72rem', fontWeight: 700 }}
                          >
                            Agendar
                          </Button>
                        )}
                        {orc.status === 'fechado' && orc.saldoAberto > 0 && (
                          <Button
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleAbrirPagamento(orc); }}
                            sx={{ color: '#dc2626', fontSize: '0.72rem', fontWeight: 700 }}
                          >
                            Registrar
                          </Button>
                        )}
                        <Chip label={sc.label} size="small" sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 700, fontSize: '0.7rem' }} />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Aba: Financeiro */}
      {aba === 'financeiro' && (
        <Box display="flex" flexDirection="column" gap={2.5}>
          {/* Cards de resumo */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: (financeiro.saldoEmAberto > 0 || orcamentosAbertos.length > 0) ? '1fr 1fr' : '1fr' }, gap: 2.5 }}>
            <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(249,115,22,0.12)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <Typography fontSize="0.7rem" color="text.disabled" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Investido</Typography>
              <Typography fontSize="2rem" fontWeight={700} color={ORANGE}>{fmtBRL(financeiro.totalInvestido)}</Typography>
            </Paper>

            {(financeiro.saldoEmAberto > 0 || orcamentosAbertos.length > 0) && (
              <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(220,38,38,0.18)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <Typography fontSize="0.7rem" fontWeight={700} color="#dc2626" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Saldo em Aberto</Typography>
                <Typography fontSize="2rem" fontWeight={700} color="#dc2626">
                  {fmtBRL(financeiro.saldoEmAberto || orcamentosAbertos.reduce((s, o) => s + (o.saldoAberto || 0), 0))}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.3} mb={orcamentosAbertos.length ? 1.5 : 0}>
                  <Typography fontSize="0.72rem" color="text.secondary">Referente a orçamentos fechados ainda não quitados</Typography>
                  <Typography component="button" onClick={() => setAba('orcamentos')} fontSize="0.72rem" fontWeight={700} color={ORANGE} sx={{ border: 0, bgcolor: 'transparent', cursor: 'pointer', p: 0, whiteSpace: 'nowrap' }}>
                    Ver orçamentos →
                  </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: orcamentosAbertos.length > 1 ? '1fr 1fr' : '1fr' }, gap: 1 }}>
                  {orcamentosAbertos.map(orc => (
                    <Box key={orc.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, p: 1.25, borderRadius: 2, bgcolor: isDarkMode ? 'rgba(220,38,38,0.08)' : 'rgba(220,38,38,0.05)' }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontSize="0.8rem" fontWeight={600} noWrap>{fmtBRL(orc.saldoAberto)} em aberto</Typography>
                        <Typography fontSize="0.7rem" color="text.disabled">Orçamento de {fmtData(orc.createdAt)}</Typography>
                      </Box>
                      <Button
                        size="small"
                        onClick={() => handleAbrirPagamento(orc)}
                        sx={{ color: '#dc2626', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}
                      >
                        Registrar
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}
          </Box>

          {/* Gráfico de evolução */}
          {financeiro.porMes && financeiro.porMes.some(m => m.valor > 0) && (
            <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(249,115,22,0.12)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <Typography fontWeight={700} fontSize="1rem" sx={{ borderLeft: '3px solid #F97316', pl: 1.5, mb: 2.5 }}>Evolução Financeira</Typography>
              <Typography fontSize="0.7rem" fontWeight={700} color="text.disabled" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                Últimos 6 meses
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={financeiro.porMes} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#404040' : '#e2e8f0'} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: isDarkMode ? '#a0a0a0' : '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: isDarkMode ? '#a0a0a0' : '#94a3b8' }} axisLine={false} tickLine={false} width={40} />
                  <ChartTooltip formatter={(v) => fmtBRL(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="valor" name="Faturado" fill={ORANGE} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Últimos pagamentos — grade usando a largura toda */}
          {financeiro.historico && financeiro.historico.length > 0 && (
            <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(249,115,22,0.12)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <Typography fontWeight={700} fontSize="1rem" sx={{ borderLeft: '3px solid #F97316', pl: 1.5, mb: 2 }}>
                Últimos Pagamentos
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 1.25 }}>
                {financeiro.historico.slice(0, 9).map((h) => (
                  <Box key={h.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, p: 1.5, borderRadius: 2, bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontSize="0.82rem" fontWeight={600} noWrap>{h.descricao || h.procedimento || 'Pagamento'}</Typography>
                      <Typography fontSize="0.7rem" color="text.disabled">{fmtData(h.data)} • {h.formaPagamento}</Typography>
                    </Box>
                    <Typography fontSize="0.82rem" fontWeight={700} color={ORANGE} sx={{ whiteSpace: 'nowrap', ml: 1 }}>{fmtBRL(h.valor)}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </Box>
      )}

      {/* Aba: Documentos */}
      {aba === 'documentos' && (
        <Box display="flex" flexDirection="column" gap={3}>

          {/* Documentação / Anamnese */}
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(249,115,22,0.12)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography fontWeight={700} fontSize="1rem" sx={{ borderLeft: '3px solid #F97316', pl: 1.5 }}>Documentação</Typography>
              <Button size="small" startIcon={<EditNoteIcon />} variant="outlined" onClick={() => { setEnvTermoId(''); setEnvVia('link'); setErroEnviar(''); setLinkGerado(''); setModalEnviarAnamnese(true); }} sx={{ borderColor: ORANGE, color: ORANGE, borderRadius: 2, fontSize: '0.72rem' }}>
                Enviar Anamnese
              </Button>
            </Box>

            {termosAssinados.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <ReceiptLongIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.disabled" fontSize="0.85rem">Nenhum termo assinado ainda.</Typography>
              </Box>
            ) : (
              <Box mt={2}>
                <Typography fontSize="0.78rem" fontWeight={700} color="text.disabled" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                  Termos Assinados
                </Typography>
                <Box display="flex" flexDirection="column" gap={0.75}>
                  {termosAssinados.map(d => (
                    <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '8px 12px', borderRadius: 2, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                      <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 18, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontSize="0.82rem" fontWeight={600} noWrap>{d.termo?.titulo || 'Termo'}</Typography>
                        <Typography fontSize="0.72rem" color="text.disabled">
                          {d.nomeAssinante} • {fmtData(d.assinadoEm)}
                        </Typography>
                      </Box>
                      {d.autentiqueId && (
                        <Chip label="Digital" size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#dcfce7', color: '#15803d', mr: 0.5 }} />
                      )}
                      {d.autentiqueId && (
                        <IconButton
                          size="small"
                          title="Baixar documento assinado"
                          onClick={async () => {
                            try {
                              const resp = await api.get(`/termos/documentos/${d.id}/sincronizar`);
                              const url = resp?.data?.downloadUrl || resp?.downloadUrl;
                              if (url) window.open(url, '_blank');
                            } catch { /* silencioso */ }
                          }}
                          sx={{ color: '#16a34a' }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Paper>

          {/* Arquivos */}
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(249,115,22,0.12)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Typography fontWeight={700} fontSize="1rem" sx={{ borderLeft: '3px solid #F97316', pl: 1.5, mb: 2.5 }}>Arquivos</Typography>

            <Box display="flex" gap={1} mb={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Nome da nova pasta (ex: Raio-X)"
                value={novaPastaNome}
                onChange={(e) => setNovaPastaNome(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCriarPasta(); }}
                disabled={criandoPasta}
              />
              <Button
                variant="outlined"
                startIcon={criandoPasta ? <CircularProgress size={16} /> : <CreateNewFolderIcon />}
                onClick={handleCriarPasta}
                disabled={criandoPasta || !novaPastaNome.trim()}
                sx={{ whiteSpace: 'nowrap', color: ORANGE, borderColor: 'rgba(249,115,22,0.4)', '&:hover': { borderColor: ORANGE, bgcolor: 'rgba(249,115,22,0.06)' } }}
              >
                Criar pasta
              </Button>
            </Box>
            {erroArquivo && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroArquivo('')}>{erroArquivo}</Alert>}

            {Object.keys(arquivos.reduce((acc, a) => ({ ...acc, [a.pasta || 'Geral']: true }), {})).length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <FolderIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.disabled" fontSize="0.9rem">Nenhuma pasta criada ainda.</Typography>
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" gap={2}>
                {Object.entries(arquivos.reduce((acc, a) => {
                  const k = a.pasta || 'Geral';
                  if (!acc[k]) acc[k] = [];
                  acc[k].push(a);
                  return acc;
                }, {})).map(([pasta, itens]) => {
                  const arquivosReais = itens.filter(a => a.nomeArquivo);
                  const inputId = `upload-${pasta.replace(/\s+/g, '-')}`;
                  return (
                    <Box key={pasta} sx={{ borderRadius: 2, border: '1px solid rgba(249,115,22,0.15)', overflow: 'hidden' }}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25,
                        bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#FFF7ED',
                      }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <FolderIcon sx={{ fontSize: 18, color: ORANGE }} />
                          <Typography fontWeight={700} fontSize="0.85rem">{pasta}</Typography>
                          <Typography fontSize="0.72rem" color="text.disabled">({arquivosReais.length})</Typography>
                        </Box>
                        <Button
                          size="small"
                          component="label"
                          startIcon={uploadingPasta === pasta ? <CircularProgress size={14} /> : <CloudUploadIcon sx={{ fontSize: 16 }} />}
                          disabled={uploadingPasta === pasta}
                          sx={{ fontSize: '0.72rem', textTransform: 'none', color: ORANGE }}
                        >
                          Enviar arquivo
                          <input
                            id={inputId}
                            type="file"
                            hidden
                            onChange={(e) => { handleUploadArquivo(pasta, e.target.files[0]); e.target.value = ''; }}
                          />
                        </Button>
                        <IconButton size="small" onClick={() => handleExcluirPasta(pasta)} sx={{ color: '#dc2626' }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {arquivosReais.length === 0 ? (
                        <Typography sx={{ px: 2, py: 1.5 }} fontSize="0.8rem" color="text.disabled">Pasta vazia.</Typography>
                      ) : (
                        <Box>
                          {arquivosReais.map(a => (
                            <Box
                              key={a.id}
                              sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                px: 2, py: 1, borderTop: '1px solid rgba(249,115,22,0.08)',
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                                <InsertDriveFileIcon sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography fontSize="0.82rem" fontWeight={600} noWrap>{a.nomeArquivo}</Typography>
                                  <Typography fontSize="0.68rem" color="text.disabled">
                                    {a.autor?.nome || 'Usuário'} · {fmtData(a.createdAt)}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box display="flex" gap={0.5} flexShrink={0}>
                                <IconButton size="small" onClick={() => handleBaixarArquivo(a.id)} sx={{ color: ORANGE }}>
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleExcluirArquivo(a.id)} sx={{ color: '#dc2626' }}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Aba: Observações */}
      {aba === 'observacoes' && (
        <Box display="flex" flexDirection="column" gap={3}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(249,115,22,0.12)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Typography fontWeight={700} fontSize="1rem" sx={{ borderLeft: '3px solid #F97316', pl: 1.5, mb: 2.5 }}>Observações</Typography>

            <Box display="flex" gap={1} mb={3} alignItems="flex-start">
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                placeholder="Registrar uma observação sobre esta consulta..."
                value={novaAnotacao}
                onChange={(e) => setNovaAnotacao(e.target.value)}
                disabled={salvandoAnotacao}
              />
              <Button
                variant="contained"
                onClick={handleAdicionarAnotacao}
                disabled={salvandoAnotacao || !novaAnotacao.trim()}
                sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#EA580C' }, whiteSpace: 'nowrap', height: 'fit-content', mt: 0.25 }}
              >
                {salvandoAnotacao ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Adicionar'}
              </Button>
            </Box>
            {erroAnotacao && <Alert severity="error" sx={{ mb: 2 }}>{erroAnotacao}</Alert>}

            {anotacoes.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <EditNoteIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.disabled" fontSize="0.9rem">Nenhuma observação registrada ainda.</Typography>
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" gap={1.5}>
                {anotacoes.map(nota => (
                  <Box
                    key={nota.id}
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#FFF7ED',
                      border: '1px solid rgba(249,115,22,0.12)',
                    }}
                  >
                    <Typography fontSize="0.9rem" sx={{ whiteSpace: 'pre-wrap', mb: 0.75 }}>{nota.texto}</Typography>
                    <Typography fontSize="0.72rem" fontWeight={600} color="text.secondary">
                      {nota.autor?.nome || 'Usuário'} · {fmtData(nota.createdAt)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Modal Enviar Anamnese via Autentique */}
      <Dialog open={modalEnviarAnamnese} onClose={() => setModalEnviarAnamnese(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Enviar Anamnese — {paciente.nome}
          <IconButton size="small" onClick={() => setModalEnviarAnamnese(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {linkGerado ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>Anamnese enviada com sucesso!</Alert>
              <Typography fontSize="0.82rem" fontWeight={600} mb={0.5}>Link para assinatura:</Typography>
              <TextField fullWidth size="small" value={linkGerado} InputProps={{ readOnly: true }} onClick={e => e.target.select()} />
            </Box>
          ) : (
            <>
              {termoAnamneses.length === 0 ? (
                <Alert severity="warning">Nenhuma anamnese cadastrada. Crie uma em Termos &amp; Documentos.</Alert>
              ) : (
                <>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Modelo de Anamnese</InputLabel>
                    <Select value={envTermoId} label="Modelo de Anamnese" onChange={e => setEnvTermoId(e.target.value)}>
                      {termoAnamneses.map(t => <MenuItem key={t.id} value={t.id}>{t.titulo}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel>Enviar via</InputLabel>
                    <Select value={envVia} label="Enviar via" onChange={e => setEnvVia(e.target.value)}>
                      <MenuItem value="link">Copiar link</MenuItem>
                      <MenuItem value="whatsapp">WhatsApp</MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}
              {erroEnviar && <Alert severity="error" sx={{ mt: 1.5 }}>{erroEnviar}</Alert>}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setModalEnviarAnamnese(false)} size="small">Fechar</Button>
          {!linkGerado && (
            <Button onClick={handleEnviarAnamnese} variant="contained" size="small" disabled={enviando || termoAnamneses.length === 0} sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#ea6c0a' } }}>
              {enviando ? 'Enviando...' : 'Enviar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Registrar pagamento de saldo em aberto */}
      <ModalPagamento
        open={modalPagamentoAberto}
        onClose={() => { setModalPagamentoAberto(false); setOrcamentoSelecionado(null); }}
        valorTotal={orcamentoSelecionado?.valorTotal}
        valorJaPago={orcamentoSelecionado?.valorPago || 0}
        tipoUsuario={tipoUsuario}
        onSave={handleSalvarPagamentoOrcamento}
      />

      {/* Criar/editar orçamento */}
      <ModalOrcamento
        open={modalOrcamentoAberto}
        onClose={() => { setModalOrcamentoAberto(false); setOrcamentoEmEdicao(null); }}
        procedimentos={procedimentosLista}
        pacienteTelefone={paciente.telefone}
        pacienteNome={paciente.nome}
        orcamentoData={orcamentoEmEdicao}
        onSave={handleSalvarOrcamento}
        onDelete={orcamentoEmEdicao ? handleExcluirOrcamento : undefined}
        onEnviarAnamnese={() => {
          setModalOrcamentoAberto(false);
          setEnvTermoId('');
          setErroEnviar('');
          setLinkGerado('');
          setModalEnviarAnamnese(true);
        }}
        somenteLeitura={orcamentoEmEdicao && (orcamentoEmEdicao.status === 'fechado' || orcamentoEmEdicao.status === 'nao_fechado')}
      />

      {/* Orçamento fechado agora — oferece já marcar a consulta */}
      <Dialog open={!!orcamentoParaAgendar} onClose={() => setOrcamentoParaAgendar(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>Orçamento fechado! 🎉</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" fontSize="0.9rem">
            Quer marcar a consulta agora? O pagamento é registrado depois, quando o paciente comparecer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOrcamentoParaAgendar(null)} sx={{ color: 'text.secondary' }}>Agora não</Button>
          <Button
            variant="contained"
            onClick={() => navigate('/agenda', { state: { novoAgendamento: {
              pacienteId: id,
              procedimentos: orcamentoParaAgendar?.procedimentos || [],
              orcamentoId: orcamentoParaAgendar?.id,
            } } })}
            sx={{ bgcolor: ORANGE, borderRadius: 2, fontWeight: 700, '&:hover': { bgcolor: '#ea6c0a' } }}
          >
            Agendar agora
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FichaPaciente;
